/* ==========================================================================
   LEE-THAL THEME
   Self-contained modules, each safe to run even if its markup is absent from
   the page (sections can be reordered, removed, or not present on a given
   template): A. molten-gold WebGL hero field, B. latte-fill wordmark,
   C. tilt product cards, D. scroll reveal, E. ingredient marquee, F. feature
   accordion, G. header nav + search, H. product variant picker.
   Respects prefers-reduced-motion and pauses when off-screen / tab hidden.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Shared, smoothed pointer position over the hero stage (0..1 across the box).
  // Both the WebGL field and the latte wordmark read from this. `energy` is a
  // 0..1 mouse-activity level that drives the molten field: movement eases it
  // toward 1, idle eases it back toward 0 (near-black).
  var pointer = { x: 0.5, y: 0.4, tx: 0.5, ty: 0.4, speed: 0, energy: 0.85, lastMove: -9999, lastX: 0.5, lastY: 0.4 };

  var stage = document.querySelector('.stage');

  if (stage) {
    stage.addEventListener('pointermove', function (e) {
      var r = stage.getBoundingClientRect();
      var nx = (e.clientX - r.left) / r.width;
      var ny = (e.clientY - r.top) / r.height;
      var dx = nx - pointer.lastX, dy = ny - pointer.lastY;
      pointer.speed = Math.min(1, Math.sqrt(dx * dx + dy * dy) * 6);
      pointer.lastMove = performance.now();
      pointer.tx = nx; pointer.ty = ny;
      pointer.lastX = nx; pointer.lastY = ny;
    }, { passive: true });
  }

  /* ======================================================================
     A. MOLTEN-GOLD WEBGL FIELD
     ====================================================================== */
  var GoldField = (function () {
    var renderer, scene, camera, material, canvas;
    var ok = false;

    var frag = [
      'precision highp float;',
      'varying vec2 vUv;',
      'uniform float uTime; uniform vec2 uMouse; uniform vec2 uRes; uniform float uReduced; uniform float uEnergy;',
      'float hash(vec2 p){ p=fract(p*vec2(234.34,435.345)); p+=dot(p,p+34.23); return fract(p.x*p.y); }',
      'float noise(vec2 p){ vec2 i=floor(p),f=fract(p); vec2 u=f*f*(3.0-2.0*f);',
      '  return mix(mix(hash(i),hash(i+vec2(1.,0.)),u.x), mix(hash(i+vec2(0.,1.)),hash(i+vec2(1.,1.)),u.x),u.y); }',
      'float fbm(vec2 p){ float v=0.0,a=0.5; for(int i=0;i<5;i++){ v+=a*noise(p); p*=2.02; a*=0.5; } return v; }',
      'void main(){',
      '  float aspect=uRes.x/max(uRes.y,1.0);',
      '  vec2 p=vUv; p.x*=aspect;',
      '  float t = uReduced>0.5 ? 8.0 : uTime*0.05;',
      '  vec2 q=vec2(fbm(p*1.6+vec2(0.0,t)), fbm(p*1.6+vec2(5.2,-t)));',
      '  vec2 r=vec2(fbm(p*1.6+3.0*q+vec2(1.7,9.2)+t*0.5), fbm(p*1.6+3.0*q+vec2(8.3,2.8)-t*0.5));',
      '  float n=fbm(p*1.6+2.5*r);',
      '  vec2 m=uMouse; m.x*=aspect; float glow=smoothstep(0.9,0.0,distance(p,m))*0.6;',
      '  vec3 c0=vec3(0.016,0.016,0.021);',
      '  vec3 c1=vec3(0.105,0.078,0.040);',
      '  vec3 c2=vec3(0.540,0.400,0.205);',
      '  vec3 c3=vec3(0.880,0.775,0.560);',
      '  float f=pow(n,1.78);',
      '  vec3 gold=mix(c0,c1,smoothstep(0.16,0.52,f));',
      '  gold=mix(gold,c2,smoothstep(0.48,0.80,f));',
      '  gold=mix(gold,c3,smoothstep(0.78,0.98,f)*0.85);',
      '  float streak=pow(max(0.0,sin((r.x+r.y)*6.2831+t*3.0)),8.0);',
      '  gold+=c3*streak*0.13;',
      '  vec3 base=mix(vec3(0.022,0.017,0.011), vec3(0.005,0.005,0.008), smoothstep(0.0,0.95,length((vUv-vec2(0.5,0.62))*vec2(1.0,1.12))));',
      '  float e=uEnergy*uEnergy*(3.0-2.0*uEnergy);',
      '  vec3 col=mix(base, gold, e);',
      '  col+=vec3(0.55,0.42,0.22)*glow*e*(0.4+f);',
      '  vec2 vc=vUv-0.5; float vig=smoothstep(0.99,0.28,length(vc*vec2(1.05,1.25)));',
      '  col*=mix(0.42,1.02,vig);',
      '  gl_FragColor=vec4(col,1.0);',
      '}'
    ].join('\n');

    var vert = 'varying vec2 vUv; void main(){ vUv=uv; gl_Position=vec4(position.xy,0.0,1.0); }';

    function init() {
      if (typeof THREE === 'undefined' || !stage) return false;
      canvas = stage.querySelector('[data-webgl]');
      if (!canvas) return false;
      try {
        renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: false, alpha: false, powerPreference: 'high-performance' });
      } catch (err) {
        return false;
      }
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));

      scene = new THREE.Scene();
      camera = new THREE.Camera();

      material = new THREE.ShaderMaterial({
        uniforms: {
          uTime: { value: 0 },
          uMouse: { value: new THREE.Vector2(0.5, 0.6) },
          uRes: { value: new THREE.Vector2(1, 1) },
          uReduced: { value: REDUCED ? 1 : 0 },
          uEnergy: { value: REDUCED ? 0.6 : pointer.energy }
        },
        vertexShader: vert,
        fragmentShader: frag
      });
      scene.add(new THREE.Mesh(new THREE.PlaneGeometry(2, 2), material));

      resize();
      ok = true;
      render();
      return true;
    }

    function resize() {
      if (!ok && !renderer) return;
      var w = stage.clientWidth, h = stage.clientHeight;
      renderer.setSize(w, h, false);
      material.uniforms.uRes.value.set(w, h);
    }

    function render() { renderer.render(scene, camera); }

    function update(ts) {
      if (!ok) return;
      material.uniforms.uTime.value = ts * 0.001;
      material.uniforms.uMouse.value.set(pointer.x, 1 - pointer.y);
      material.uniforms.uEnergy.value = pointer.energy;
      render();
    }

    return { init: init, resize: resize, update: update, get ok() { return ok; } };
  })();

  /* ======================================================================
     B. LATTE-FILL WORDMARK
     ====================================================================== */
  var Latte = (function () {
    var fill = document.getElementById('latteFill');
    var foam = document.getElementById('latteFoam');
    var shine = document.getElementById('latteShine');

    var W = 440, BOTTOM = 100;
    var REST_Y = 58;
    var SAMPLES = 44;

    var tilt = 0, tiltVel = 0;
    var slosh = 0;
    var phase = 0;

    function build(points, topOffset, botOffset) {
      var d = 'M0,' + (points[0] + topOffset).toFixed(1);
      for (var i = 1; i < points.length; i++) {
        var x = (i / (points.length - 1)) * W;
        d += ' L' + x.toFixed(1) + ',' + (points[i] + topOffset).toFixed(1);
      }
      if (botOffset === undefined) {
        d += ' L' + W + ',' + BOTTOM + ' L0,' + BOTTOM + ' Z';
      } else {
        for (var j = points.length - 1; j >= 0; j--) {
          var xx = (j / (points.length - 1)) * W;
          d += ' L' + xx.toFixed(1) + ',' + (points[j] + botOffset).toFixed(1);
        }
        d += ' Z';
      }
      return d;
    }

    function update(dt, ts) {
      var target = (pointer.x - 0.5) * 2;
      tiltVel += (target - tilt) * 9 * dt;
      tiltVel *= Math.pow(0.0025, dt);
      tilt += tiltVel * dt;

      slosh += pointer.speed * 0.9;
      slosh *= Math.pow(0.06, dt);
      pointer.speed *= 0.9;
      phase += (2.2 + slosh * 2.5) * dt;

      var idle = 0.8 + 0.5 * Math.sin(ts * 0.0012);
      var amp = Math.min(10, idle + slosh * 12);
      var edge = tilt * 10;

      var pts = new Array(SAMPLES);
      for (var i = 0; i < SAMPLES; i++) {
        var u = i / (SAMPLES - 1);
        var lean = edge * (u - 0.5) * 2;
        var wave = Math.sin(u * 6.283 * 1.3 + phase) * amp
                 + Math.sin(u * 6.283 * 2.6 - phase * 1.4) * amp * 0.35;
        pts[i] = REST_Y + lean + wave * 0.42;
      }

      fill.setAttribute('d', build(pts, 0));
      foam.setAttribute('d', build(pts, -2, 2));
      shine.setAttribute('d', build(pts, 3, 12));
    }

    function rest() {
      var pts = [];
      for (var i = 0; i < SAMPLES; i++) pts.push(REST_Y);
      fill.setAttribute('d', build(pts, 0));
      foam.setAttribute('d', build(pts, -2, 2));
      shine.setAttribute('d', build(pts, 3, 12));
    }

    return { update: update, rest: rest, ready: !!(fill && foam && shine) };
  })();

  /* ======================================================================
     C. TILT PRODUCT CARDS
     ====================================================================== */
  function initTiltCards() {
    var MAX = 16;

    function settleCard(state, target, rx, ry) {
      var vx = 0, vy = 0;
      var k = 210, damp = 15;
      var last = performance.now();
      target.style.transition = 'none';
      function step(now) {
        var dt = Math.min((now - last) / 1000, 0.04); last = now;
        vx += (-k * rx - damp * vx) * dt;
        vy += (-k * ry - damp * vy) * dt;
        rx += vx * dt; ry += vy * dt;
        target.style.transform = 'perspective(850px) rotateX(' + rx.toFixed(3) + 'deg) rotateY(' + ry.toFixed(3) + 'deg)';
        if (Math.abs(rx) < 0.03 && Math.abs(ry) < 0.03 && Math.abs(vx) < 0.4 && Math.abs(vy) < 0.4) {
          target.style.transform = 'perspective(850px) rotateX(0deg) rotateY(0deg)';
          state._settleRaf = null; return;
        }
        state._settleRaf = requestAnimationFrame(step);
      }
      state._settleRaf = requestAnimationFrame(step);
    }

    document.querySelectorAll('.js-tilt').forEach(function (scene) {
      var card = scene.querySelector('.product__card');
      // The tilt transform is applied to this wrapper, never to the <a>
      // itself - a link whose own box keeps rotating in 3D can end up with
      // its hit region out of sync with what's painted, so clicks on it get
      // dropped intermittently. Keeping .product__card flat means its hit
      // box always matches its layout box, and the tilt is purely visual.
      var tilt = scene.querySelector('.product__tilt');
      var glare = scene.querySelector('.product__glare');
      if (!card || !tilt) return;
      card._rx = 0; card._ry = 0;

      var IDLE_MS = 700;
      var MAX_MS = 1600;

      function relax() {
        if (glare) glare.style.opacity = '0';
        settleCard(card, tilt, card._rx, card._ry);
      }

      if (!REDUCED) {
        card._start = 0;
        card._held = false;

        scene.addEventListener('pointermove', function (e) {
          if (card._held) return;
          var now = performance.now();
          if (!card._start) card._start = now;

          if (now - card._start >= MAX_MS) {
            clearTimeout(card._idle);
            card._held = true; card._start = 0;
            relax();
            return;
          }

          if (card._settleRaf) { cancelAnimationFrame(card._settleRaf); card._settleRaf = null; }
          var r = card.getBoundingClientRect();
          var px = (e.clientX - r.left) / r.width;
          var py = (e.clientY - r.top) / r.height;
          var ry = (px - 0.5) * 2 * MAX;
          var rx = (0.5 - py) * 2 * MAX;
          card._rx = rx; card._ry = ry;
          tilt.style.transition = 'transform .08s linear';
          tilt.style.transform = 'perspective(850px) rotateX(' + rx.toFixed(2) + 'deg) rotateY(' + ry.toFixed(2) + 'deg)';
          if (glare) {
            glare.style.setProperty('--mx', (px * 100).toFixed(1) + '%');
            glare.style.setProperty('--my', (py * 100).toFixed(1) + '%');
            glare.style.opacity = '1';
          }

          clearTimeout(card._idle);
          card._idle = setTimeout(function () { card._start = 0; relax(); }, IDLE_MS);
        });

        scene.addEventListener('pointerleave', function () {
          clearTimeout(card._idle);
          card._held = false;
          card._start = 0;
          relax();
          card.classList.remove('is-landing');
          void card.offsetWidth;
          card.classList.add('is-landing');
        });
      }

      card.addEventListener('pointerdown', function () {
        if (REDUCED) return;
        card.classList.remove('is-settling');
        void card.offsetWidth;
        card.classList.add('is-settling');
      });
      card.addEventListener('animationend', function () { card.classList.remove('is-settling', 'is-landing'); });
    });
  }

  /* ======================================================================
     D. SCROLL REVEAL
     ====================================================================== */
  function initReveal() {
    var items = document.querySelectorAll('.reveal');
    if (REDUCED || !('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('in'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ======================================================================
     E. INGREDIENT MARQUEE
     Flavour note + "used in" tags come from data-desc / data-blends on each
     .marquee__item (set by the ingredient-marquee section's schema blocks),
     not a hardcoded JS lookup - so merchants can edit ingredients in the
     theme editor without touching this file.
     ====================================================================== */
  function initMarquee() {
    var marquee = document.querySelector('.marquee');
    var section = marquee ? marquee.closest('.ingredients') : null;
    if (!marquee || !section) return;
    var pop = section.querySelector('.ing-pop');
    var openWord = null;

    function closePop() {
      if (!pop) return;
      pop.hidden = true;
      marquee.classList.remove('is-frozen');
      if (openWord) openWord.classList.remove('is-active');
      openWord = null;
    }

    function openPop(word) {
      if (!pop) return;
      var desc = word.dataset.desc;
      var blends = (word.dataset.blends || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);

      pop.querySelector('.ing-pop__name').textContent = word.textContent.trim();
      pop.querySelector('.ing-pop__desc').textContent = desc || '';
      var wrap = pop.querySelector('.ing-pop__blends');
      wrap.innerHTML = '';
      if (blends.length) {
        var label = document.createElement('span');
        label.className = 'ing-pop__label';
        label.textContent = 'In our blends';
        wrap.appendChild(label);
        blends.forEach(function (b) {
          var s = document.createElement('span');
          s.className = 'ing-pop__blend';
          s.textContent = b;
          wrap.appendChild(s);
        });
      }

      marquee.classList.add('is-frozen');
      pop.hidden = false;
      var sRect = section.getBoundingClientRect();
      var wRect = word.getBoundingClientRect();
      var left = wRect.left - sRect.left + wRect.width / 2;
      var top = wRect.bottom - sRect.top + 12;
      var half = pop.offsetWidth / 2;
      left = Math.max(half + 10, Math.min(sRect.width - half - 10, left));
      pop.style.left = left + 'px';
      pop.style.top = top + 'px';

      if (openWord) openWord.classList.remove('is-active');
      openWord = word;
      word.classList.add('is-active');
    }

    marquee.addEventListener('click', function (e) {
      var word = e.target.closest('.marquee__item');
      if (!word) return;
      word.classList.remove('pop');
      void word.offsetWidth;
      word.classList.add('pop');
      if (openWord === word) closePop(); else openPop(word);
    });
    marquee.addEventListener('animationend', function (e) {
      if (e.target.classList && e.target.classList.contains('pop')) e.target.classList.remove('pop');
    });

    if (pop) {
      pop.querySelector('.ing-pop__close').addEventListener('click', closePop);
      document.addEventListener('click', function (e) {
        if (pop.hidden) return;
        if (!pop.contains(e.target) && !e.target.closest('.marquee__item')) closePop();
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closePop(); });
      window.addEventListener('scroll', function () { if (!pop.hidden) closePop(); }, { passive: true });
    }
  }

  /* ======================================================================
     F. FEATURE ACCORDION
     ====================================================================== */
  function initFeatures() {
    document.querySelectorAll('.js-feature').forEach(function (feature) {
      var btn = feature.querySelector('.feature__more-btn');
      var label = feature.querySelector('.feature__more-label');
      if (!btn) return; // no expanded text configured for this block

      function toggle() {
        var open = feature.classList.toggle('open');
        btn.setAttribute('aria-expanded', String(open));
        if (label) label.textContent = open ? 'Show less' : 'Read more';
      }

      feature.addEventListener('click', function (e) {
        if (e.target.closest('.feature__more-btn')) return;
        toggle();
      });
      btn.addEventListener('click', function (e) { e.stopPropagation(); toggle(); });
    });
  }

  /* ======================================================================
     G. HEADER NAV + SEARCH TOGGLE
     ====================================================================== */
  function initHeader() {
    var header = document.querySelector('.site-header');
    if (!header) return;

    var burger = header.querySelector('.nav__burger');
    var navLinks = header.querySelector('#nav-links');
    if (burger && navLinks) {
      burger.addEventListener('click', function () {
        var open = navLinks.classList.toggle('is-open');
        burger.setAttribute('aria-expanded', String(open));
      });
    }

    var searchToggle = header.querySelector('.js-search-toggle');
    var searchForm = header.querySelector('#header-search');
    if (searchToggle && searchForm) {
      searchToggle.addEventListener('click', function () {
        var open = searchForm.hidden;
        searchForm.hidden = !open;
        searchToggle.setAttribute('aria-expanded', String(open));
        if (open) searchForm.querySelector('input[type="search"]').focus();
      });
    }
  }

  /* ======================================================================
     G2. PRODUCT GALLERY THUMBNAILS
     ====================================================================== */
  function initProductGallery() {
    var mainImage = document.querySelector('[data-main-image]');
    var thumbs = document.querySelectorAll('[data-thumb]');
    if (!mainImage || !thumbs.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        mainImage.src = thumb.dataset.src;
        mainImage.srcset = '';
        thumbs.forEach(function (t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });
  }

  /* ======================================================================
     H. PRODUCT VARIANT PICKER
     Reads the product JSON embedded by sections/main-product.liquid, keeps
     the hidden variant id input and price display in sync with the option
     selects, and disables add-to-cart when the matched variant is sold out.
     ====================================================================== */
  function initProductForm() {
    var form = document.querySelector('[data-product-form]');
    if (!form) return;
    var json = document.querySelector('[data-product-json]');
    if (!json) return;

    var product;
    try { product = JSON.parse(json.textContent); } catch (err) { return; }

    var selects = Array.prototype.slice.call(form.querySelectorAll('[data-option-index]'));
    var variantInput = form.querySelector('[data-variant-id]');
    var priceAmountEl = document.querySelector('[data-price-amount]');
    var priceCompareEl = document.querySelector('[data-price-compare]');
    var addBtn = form.querySelector('[data-add-to-cart]');
    var addText = form.querySelector('[data-add-to-cart-text]');
    var currencyCode = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'NZD';

    function currentOptions() {
      return selects.map(function (select) { return select.value; });
    }

    function findVariant(options) {
      return product.variants.find(function (variant) {
        return variant.options.every(function (value, i) { return value === options[i]; });
      });
    }

    function money(cents) {
      return (cents / 100).toLocaleString(document.documentElement.lang || undefined, { style: 'currency', currency: currencyCode });
    }

    function sync() {
      var variant = findVariant(currentOptions());
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;
      if (priceAmountEl) priceAmountEl.textContent = money(variant.price);
      if (priceCompareEl) {
        var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        priceCompareEl.hidden = !onSale;
        if (onSale) priceCompareEl.textContent = money(variant.compare_at_price);
      }
      if (addBtn) addBtn.disabled = !variant.available;
      if (addText) addText.textContent = variant.available ? addText.dataset.available : addText.dataset.soldOut;
    }

    selects.forEach(function (select) {
      select.addEventListener('change', sync);
    });
  }

  /* ======================================================================
     MAIN LOOP
     ====================================================================== */
  var running = false, rafId = null, lastTs = 0;

  function loop(ts) {
    if (!running) return;
    var dt = Math.min((ts - lastTs) / 1000, 0.05);
    lastTs = ts;

    pointer.x += (pointer.tx - pointer.x) * 0.09;
    pointer.y += (pointer.ty - pointer.y) * 0.09;

    var ENERGY_RISE = 0.30;
    var ENERGY_FALL = 0.70;
    var target = (ts - pointer.lastMove) < 160 ? 1 : 0;
    var k = (target > pointer.energy) ? ENERGY_RISE : ENERGY_FALL;
    pointer.energy = target + (pointer.energy - target) * Math.pow(k, dt);

    GoldField.update(ts);
    if (Latte.ready) Latte.update(dt, ts);

    rafId = requestAnimationFrame(loop);
  }

  function start() {
    if (running || REDUCED) return;
    running = true; lastTs = performance.now();
    rafId = requestAnimationFrame(loop);
  }
  function stop() {
    running = false;
    if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
  }

  /* ======================================================================
     BOOT
     ====================================================================== */
  function boot() {
    var effectEnabled = stage && stage.querySelector('[data-webgl]');
    if (effectEnabled) GoldField.init();
    initTiltCards();
    initReveal();
    initMarquee();
    initFeatures();
    initHeader();
    initProductForm();
    initProductGallery();

    if (REDUCED) {
      if (Latte.ready) Latte.rest();
      return;
    }

    if (effectEnabled && stage && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries[0].isIntersecting ? start() : stop();
      }, { threshold: 0.01 }).observe(stage);
    } else if (Latte.ready) {
      // No WebGL canvas (effect disabled or Three.js unavailable), but the
      // latte wordmark still animates on its own light loop.
      start();
    }

    document.addEventListener('visibilitychange', function () {
      document.hidden ? stop() : start();
    });

    if (effectEnabled) {
      var ro = new ResizeObserver(function () { GoldField.resize(); });
      ro.observe(stage);
      window.addEventListener('resize', function () { GoldField.resize(); });
    }
  }

  if (document.readyState === 'complete') { boot(); }
  else { window.addEventListener('load', boot); }
})();
